import { VehicleCargoVolume } from './VehicleCargoVolume'
import { VehicleDisplayBody } from './VehicleDisplayBody'
import { VehicleObstacles } from './VehicleObstacles'
import { VehicleAxleGroup } from './VehicleAxles'
import { TRUCK_CAB_LENGTH, VAN_FRONT_OVERHANG_TO_WIDTH, VanCab, VehicleCab, VehicleTractor } from './VehicleBody'
import { VehicleTowbar } from './VehicleTowbar'
import { VehicleEquipmentMarkers } from './VehicleEquipmentMarkers'
import type { Vehicle } from '../../../types/optimization'
export type { VehicleSceneTheme } from './VehicleSceneTheme'
import type { VehicleSceneTheme } from './VehicleSceneTheme'

export type VehicleModel3DContext = 'configuration' | 'result' | 'readonly'

const M = (mm: number) => mm / 1000
const NOMINAL_COUPLING_HEIGHT = 1150
const TRACTOR_FIFTH_WHEEL_RENDER_HEIGHT = 905
const SEMI_KING_PIN_RENDER_HEIGHT = 60

function dimensionMm(normalized: unknown, flat: unknown, fallback: number) {
  const normalizedValue = Number(normalized)
  const flatValue = Number(flat)
  // Nested dimensions are the explicit mm contract. Flat legacy fields can be
  // cm or mm and must never override a valid nested value by magnitude.
  if (Number.isFinite(normalizedValue) && normalizedValue > 0) return normalizedValue
  return Number.isFinite(flatValue) && flatValue > 0 ? flatValue : fallback
}

/** Géométrie métier commune, sans Canvas, caméra ni overlays de configuration. */
export function VehicleModel3D({
  vehicle,
  tractor,
  coupling,
  isTractor = false,
  context = 'readonly',
  cargoVolumeOpacity,
  showTechnicalLabels,
  showEquipmentLabels,
  bodyStyle = 'technical',
  theme,
}: {
  vehicle: Vehicle | null | undefined
  tractor?: Vehicle | null | undefined
  coupling?: { trailerKingPinPosition?: number; tractorFifthWheelPosition?: number } | null
  isTractor?: boolean
  /**
   * configuration : lecture détaillée et annotations ; result : priorité aux
   * marchandises ; readonly : véhicule seul sans annotations interactives.
   */
  context?: VehicleModel3DContext
  cargoVolumeOpacity?: number
  showTechnicalLabels?: boolean
  showEquipmentLabels?: boolean
  bodyStyle?: 'technical' | 'exterior'
  theme: VehicleSceneTheme
}) {
  const length = dimensionMm(vehicle?.dimensions?.length, vehicle?.length, 12000)
  const width = dimensionMm(vehicle?.dimensions?.width, vehicle?.width, 2500)
  const height = dimensionMm(vehicle?.dimensions?.height, vehicle?.height, 2600)
  const axleConfig = typeof vehicle?.axle_configuration === 'object' ? vehicle.axle_configuration : null
  const axles = axleConfig?.axles || []
  const rigidTruck = !isTractor && (vehicle?.type === 'RIGID_TRUCK' || vehicle?.type === 'TRUCK')
  const renderedAxles = rigidTruck
    ? axles.map((axle: any) => axle.type === 'steering'
      ? { ...axle, position: Number(axle.position ?? 0) - TRUCK_CAB_LENGTH }
      : axle)
    : vehicle?.type === 'VAN'
      ? axles.map((axle: any) => ({
        ...axle,
        position: Number(axle.position ?? 0) - width * VAN_FRONT_OVERHANG_TO_WIDTH,
      }))
    : axles
  const fifthWheel = vehicle?.fifth_wheel || (axleConfig?.kingPinPosition != null ? { position: axleConfig.kingPinPosition } : null)
  const isSemi = vehicle?.type === 'SEMI_TRAILER'
  const isDrawbar = vehicle?.type === 'TRAILER'
  const isContainer = vehicle?.type === 'CONTAINER'
  // Le type TRUCK est conservé pour les anciens profils où il désigne un
  // porteur sans la précision RIGID_TRUCK.
  const mountReeferOnCabRoof = vehicle?.type === 'VAN' || vehicle?.type === 'RIGID_TRUCK' || vehicle?.type === 'TRUCK'
  const kingPin = coupling?.trailerKingPinPosition ?? fifthWheel?.position ?? axleConfig?.kingPinPosition ?? 1500
  const tractorFifthWheel = coupling?.tractorFifthWheelPosition ?? tractor?.fifth_wheel?.position ?? (typeof tractor?.axle_configuration === 'object' ? tractor.axle_configuration?.fifthWheelPosition : undefined) ?? 4200
  const tractorOffset = kingPin - tractorFifthWheel
  const tractorWidth = dimensionMm(tractor?.dimensions?.width, tractor?.width, 2500)
  const tractorLength = dimensionMm(tractor?.dimensions?.length, tractor?.length, 8000)
  const tractorConfig = typeof tractor?.axle_configuration === 'object' ? tractor.axle_configuration : null
  // Ces deux hauteurs sont déjà acceptées dans les snapshots métier, mais ne
  // font pas encore partie du contrat Vehicle historique.
  const trailerKingPinHeight = (axleConfig as any)?.kingPinHeight ?? NOMINAL_COUPLING_HEIGHT
  const tractorFifthWheelHeight = (tractorConfig as any)?.fifthWheelHeight ?? NOMINAL_COUPLING_HEIGHT
  const visualOffsetY = M(trailerKingPinHeight - tractorFifthWheelHeight + SEMI_KING_PIN_RENDER_HEIGHT - TRACTOR_FIFTH_WHEEL_RENDER_HEIGHT)
  const resolvedCargoOpacity = cargoVolumeOpacity ?? (context === 'result' ? 0 : context === 'configuration' ? 0.08 : 0.04)
  const resolvedTechnicalLabels = showTechnicalLabels ?? context === 'configuration'
  const resolvedEquipmentLabels = showEquipmentLabels ?? context === 'configuration'

  return <><>
    {!isTractor && (bodyStyle === 'exterior' ? <VehicleDisplayBody length={length} width={width} height={height} theme={theme} /> : <VehicleCargoVolume length={length} width={width} height={height} theme={theme} bodyOpacity={resolvedCargoOpacity} />)}
    <VehicleEquipmentMarkers length={length} width={width} height={height} hasReefer={vehicle?.has_reefer_unit === true && !isTractor} isAdr={vehicle?.is_hazardous_compatible === true} showLabels={resolvedEquipmentLabels} mountReeferOnCabRoof={mountReeferOnCabRoof} />
    {renderedAxles.map((axle: any, index) => <VehicleAxleGroup key={axle.id ?? index} axle={axle} index={index} width={width} trackWidth={axleConfig?.trackWidth} showLabels={resolvedTechnicalLabels} theme={theme} />)}
    {!isTractor && <VehicleObstacles vehicle={vehicle} />}
    {isTractor ? <VehicleTractor length={length} width={width} fifthWheelX={fifthWheel?.position || axleConfig?.kingPinPosition || length * 0.65} theme={theme} /> : isDrawbar ? <VehicleTowbar width={width} theme={theme} /> : vehicle?.type === 'VAN' ? <VanCab width={width} /> : !isSemi && !isContainer ? <VehicleCab width={width} verticalOffset={rigidTruck ? -450 : 0} /> : null}
    {!isTractor && tractor && isSemi && <group position={[M(tractorOffset), visualOffsetY, 0]}><VehicleTractor length={tractorLength} width={tractorWidth} fifthWheelX={tractorFifthWheel} theme={theme} axles={tractorConfig?.axles || []} trackWidth={tractorConfig?.trackWidth} axleVerticalOffsetY={-visualOffsetY} /></group>}
    {!isTractor && fifthWheel && isSemi && <group position={[M(fifthWheel.position), M(60), M(width / 2)]}><mesh><cylinderGeometry args={[M(320), M(320), M(30), 24]} /><meshStandardMaterial color="#9333ea" metalness={0.6} roughness={0.35} /></mesh></group>}
  </></>
}
